import { useEffect, useState, type FormEvent } from 'react';
import type { PriceListDto, PriceListVariantDto } from '@ecom/contracts';
import { usePriceListsStore } from '@/stores/price-lists.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { bpsToPercent, halalasToRiyals, percentToBps, riyalsToHalalas } from '@/lib/price-input';
import { PlusIcon, SearchIcon, TagIcon } from '@/components/icons';
import { ToggleField } from '@/components/fields';
import { ListCard, ListToolbar, LoadMore, Page, Sheet, TableScroll, Table, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Field, Input, Pill, TableSkeleton } from '@/components/ui';

/**
 * Trade price lists (ADR-045, docs/01-Business/Trade.md): what a trade account
 * pays — a percentage off every retail price, and exact prices where set. A
 * list is created, changed and priced in a panel beside the list of lists.
 */

export function PriceListsPage() {
  const { items, loading, loadingMore, saving, error, nextCursor, search, load, loadMore, remove } = usePriceListsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [query, setQuery] = useState(search);
  const [editing, setEditing] = useState<PriceListDto | 'new' | null>(null);
  const [pricing, setPricing] = useState<PriceListDto | null>(null);
  const [deleting, setDeleting] = useState<PriceListDto | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (query === search) return;
    const timer = setTimeout(() => void load(query), 350);
    return () => clearTimeout(timer);
  }, [query, search, load]);

  return (
    <Page
      title={t('priceLists')}
      subtitle={t('priceListsSubtitle')}
      actions={
        <Button onClick={() => setEditing('new')}>
          <PlusIcon className="size-4" />
          {t('newPriceList')}
        </Button>
      }
      fill
    >
      <ListCard>
        <ListToolbar>
          <div className="relative w-full max-w-sm">
            <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
              <SearchIcon className="size-4" />
            </span>
            <Input
              type="search"
              placeholder={t('searchPriceLists')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label={t('searchPriceLists')}
              className="ps-9"
            />
          </div>
        </ListToolbar>

        {error && !editing && !pricing ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={5} />
        ) : items.length === 0 ? (
          <EmptyState
            title={t('noPriceLists')}
            body={t('noPriceListsHint')}
            icon={<TagIcon />}
            action={
              <Button onClick={() => setEditing('new')}>
                <PlusIcon className="size-4" />
                {t('newPriceList')}
              </Button>
            }
          />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('priceListName')}</Th>
                  <Th>{t('priceListDiscount')}</Th>
                  <Th>{t('priceListExact')}</Th>
                  <Th>{t('priceListAccounts')}</Th>
                  <Th align="end" />
                </tr>
              </thead>
              <tbody>
                {items.map((list) => (
                  <Tr key={list.id}>
                    <Td>
                      <span className="flex flex-wrap items-center gap-2 font-medium text-ink">
                        {list.name}
                        {!list.isActive ? <Pill tone="neutral">{t('inactive')}</Pill> : null}
                      </span>
                      <span className="block text-xs text-ink-muted">
                        {[list.allowsPromotions ? t('priceListWithOffers') : null, list.allowsCoupons ? t('priceListWithCodes') : null]
                          .filter(Boolean)
                          .join(' · ') || t('priceListIsTheDeal')}
                      </span>
                    </Td>
                    <Td data-numeric className="text-ink">
                      {list.discountBps > 0 ? `${bpsToPercent(list.discountBps)}%` : '—'}
                    </Td>
                    <Td data-numeric className="text-ink-muted">{count(list.itemCount, locale)}</Td>
                    <Td data-numeric className="text-ink-muted">{count(list.accountCount, locale)}</Td>
                    <Td align="end" className="whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setPricing(list)}>
                          {t('priceListPrices')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditing(list)}>
                          {t('editEntry')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(list)}>
                          {t('deleteEntry')}
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? <PriceListSheet list={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {pricing ? <PricesSheet list={pricing} onClose={() => setPricing(null)} /> : null}

      <ConfirmDialog
        open={deleting !== null}
        title={t('deletePriceListTitle')}
        body={t('deletePriceListBody')}
        confirmLabel={t('deleteEntry')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (deleting) await remove(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </Page>
  );
}

/** Create a list, or change one: its name, its percentage, what else applies on top. */
function PriceListSheet({ list, onClose }: { list: PriceListDto | null; onClose: () => void }) {
  const { create, update, saving, error } = usePriceListsStore();
  const t = useT();
  const [name, setName] = useState(list?.name ?? '');
  const [percent, setPercent] = useState(list ? bpsToPercent(list.discountBps) : '0');
  const [allowsPromotions, setAllowsPromotions] = useState(list?.allowsPromotions ?? false);
  const [allowsCoupons, setAllowsCoupons] = useState(list?.allowsCoupons ?? false);
  const [isActive, setIsActive] = useState(list?.isActive ?? true);
  const [formError, setFormError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const discountBps = percentToBps(percent);
    if (discountBps === null || discountBps >= 10_000) {
      setFormError(t('priceListDiscountHint'));
      return;
    }
    const input = { name: name.trim(), discountBps, allowsPromotions, allowsCoupons, isActive };
    const ok = list ? await update(list.id, input) : await create(input);
    if (ok) onClose();
  }

  return (
    <Sheet
      open
      title={list ? t('editPriceList') : t('newPriceList')}
      subtitle={t('priceListDialogHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" form="price-list-form" disabled={saving}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <form id="price-list-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('priceListName')} htmlFor="price-list-name">
          <Input id="price-list-name" value={name} onChange={(event) => setName(event.target.value)} required maxLength={80} />
        </Field>
        <Field label={t('priceListDiscount')} htmlFor="price-list-discount" hint={t('priceListDiscountHint')} error={formError ?? undefined}>
          <Input
            id="price-list-discount"
            dir="ltr"
            inputMode="decimal"
            value={percent}
            onChange={(event) => setPercent(event.target.value)}
            required
          />
        </Field>
        <ToggleField label={t('priceListWithOffers')} hint={t('priceListWithOffersHint')} checked={allowsPromotions} onChange={setAllowsPromotions} />
        <ToggleField label={t('priceListWithCodes')} hint={t('priceListWithCodesHint')} checked={allowsCoupons} onChange={setAllowsCoupons} />
        <ToggleField label={t('priceListActive')} hint={t('priceListActiveHint')} checked={isActive} onChange={setIsActive} />

        {error ? <Alert>{error}</Alert> : null}
      </form>
    </Sheet>
  );
}

/**
 * Every variant as this list prices it: retail, the list's percentage, and an
 * exact price staff can set or clear — searched on the server, paged.
 */
function PricesSheet({ list, onClose }: { list: PriceListDto; onClose: () => void }) {
  const { variants, variantsCursor, variantsLoading, variantSearch, exactOnly, error, loadVariants, loadMoreVariants } =
    usePriceListsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [query, setQuery] = useState('');

  useEffect(() => {
    void loadVariants(list.id, { search: '', exactOnly: false });
  }, [list.id, loadVariants]);

  useEffect(() => {
    if (query === variantSearch) return;
    const timer = setTimeout(() => void loadVariants(list.id, { search: query }), 350);
    return () => clearTimeout(timer);
  }, [query, variantSearch, list.id, loadVariants]);

  return (
    <Sheet
      open
      size="xl"
      title={list.name}
      subtitle={
        list.discountBps > 0
          ? t('priceListPricesHint').replace('{percent}', bpsToPercent(list.discountBps))
          : t('priceListPricesHintNone')
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-sm">
            <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
              <SearchIcon className="size-4" />
            </span>
            <Input
              type="search"
              placeholder={t('searchVariants')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label={t('searchVariants')}
              className="ps-9"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={exactOnly}
              onChange={(event) => void loadVariants(list.id, { exactOnly: event.target.checked })}
              className="size-4"
            />
            {t('priceListExactOnly')}
          </label>
        </div>

        {error ? <Alert>{error}</Alert> : null}

        {/* Edge to edge, so the column names pin to the top of the panel as it scrolls. */}
        <div className="-mx-5 border-t border-line">
          <Table>
            <thead>
              <tr>
                <Th>{t('product')}</Th>
                <Th align="end">{t('retailPrice')}</Th>
                <Th align="end">{t('priceListPrice')}</Th>
                <Th>{t('priceListExactPrice')}</Th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => (
                <VariantPriceRow key={variant.variantId} listId={list.id} variant={variant} locale={locale} />
              ))}
            </tbody>
          </Table>
          {!variantsLoading && variants.length === 0 ? (
            <p className="px-5 py-6 text-center text-sm text-ink-muted">{t('noResults')}</p>
          ) : null}
          <LoadMore
            visible={Boolean(variantsCursor)}
            loading={variantsLoading}
            onClick={() => void loadMoreVariants(list.id)}
          />
        </div>
      </div>
    </Sheet>
  );
}

function VariantPriceRow({ listId, variant, locale }: { listId: string; variant: PriceListVariantDto; locale: 'ar' | 'en' }) {
  const { setExact, clearExact } = usePriceListsStore();
  const t = useT();
  const [value, setValue] = useState(variant.exactPrice === null ? '' : halalasToRiyals(variant.exactPrice));
  const [busy, setBusy] = useState(false);

  async function save() {
    const trimmed = value.trim();
    const current = variant.exactPrice === null ? '' : halalasToRiyals(variant.exactPrice);
    if (trimmed === current) return;
    setBusy(true);
    if (!trimmed) {
      await clearExact(listId, variant.variantId);
    } else {
      const halalas = riyalsToHalalas(trimmed);
      if (halalas !== null) await setExact(listId, variant.variantId, halalas);
    }
    setBusy(false);
  }

  return (
    <Tr>
      <Td>
        <span className="block text-ink">{variant.productName}</span>
        <span dir="ltr" className="block text-xs text-ink-muted">{variant.sku}</span>
      </Td>
      <Td align="end" data-numeric className="text-ink-muted">{money(variant.retailPrice, locale)}</Td>
      <Td align="end" data-numeric className="text-ink">{money(variant.price, locale)}</Td>
      <Td>
        <Input
          dir="ltr"
          inputMode="decimal"
          value={value}
          placeholder={halalasToRiyals(variant.listPrice)}
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => void save()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void save();
          }}
          disabled={busy}
          aria-label={`${t('priceListExactPrice')} — ${variant.sku}`}
          className="w-32"
        />
      </Td>
    </Tr>
  );
}
